import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Button, ButtonStyle, Form, FormField, Spinner } from "@prequel/shared-components";

import EventGroups from "./EventGroups";
import WebhookDeliveryConfig from "./WebhookDeliveryConfig";

import { useCreateWebhook, useUpdateWebhook } from "@/api/queries/webhooks";
import { computeChangedFields, DELIVERY_METHODS, DeliveryMethod, ExistingWebhook, Webhook } from "@/types";

const initialWebhookState: Webhook = {
  name: "",
  vendor: Object.keys(DELIVERY_METHODS)[0] as DeliveryMethod,
  url: "",
  api_version: "2023-10-15",
  events: [],
};

const WebhookForm = ({ initialWebhook }: { initialWebhook: ExistingWebhook | undefined }) => {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [webhook, setWebhook] = useState<Webhook>(initialWebhookState);
  const isEditing = !!initialWebhook;

  const createWebhookMutation = useCreateWebhook();
  const updateWebhookMutation = useUpdateWebhook();
  const isProcessing = createWebhookMutation.isPending || updateWebhookMutation.isPending;

  useEffect(() => {
    if (initialWebhook) {
      setWebhook(initialWebhook);
    }
  }, [initialWebhook]);

  // Re-evaluate custom validity when events change
  const atLeastOneEventValidationRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (atLeastOneEventValidationRef.current) {
      atLeastOneEventValidationRef.current.setCustomValidity("");
    }
  }, [webhook.events.length]);

  const setWebhookField = (key: keyof Webhook, value: string | string[]) => {
    setWebhook((prevWebhook) => ({
      ...prevWebhook,
      [key]: value,
    }));
  };

  const handleEventChange = (event: string, isChecked: boolean) => {
    setWebhook((prevWebhook) => {
      const updatedEvents = isChecked
        ? prevWebhook.events.includes(event)
          ? prevWebhook.events
          : [...prevWebhook.events, event]
        : prevWebhook.events.filter((e) => e !== event);

      return { ...prevWebhook, events: updatedEvents };
    });
  };

  const onDeliveryMethodChange = (deliveryMethod: DeliveryMethod) => {
    setWebhook((prevWebhook) => ({
      ...prevWebhook,
      vendor: deliveryMethod,
    }));
  };

  const onSubmit = () => {
    if (isEditing && initialWebhook) {
      updateWebhookMutation.mutate({
        webhookId: initialWebhook.id,
        webhook: computeChangedFields(initialWebhook, webhook),
        redirectPath: `/webhooks/${initialWebhook.id}`,
      });
    } else {
      createWebhookMutation.mutate({
        webhook,
        redirectPath: "/webhooks",
      });
    }
  };

  return (
    <div className="pb-16">
      <Form className="space-y-4" onSubmit={onSubmit} submitButtonRef={buttonRef}>
        <FormField
          label="Name"
          id="name"
          type="text"
          subtext="Descriptive name for this webhook."
          value={webhook.name}
          onChangeHandler={(value) => setWebhookField("name", value)}
          disabled={isProcessing}
          required
        />
        <WebhookDeliveryConfig
          webhook={webhook}
          setWebhookField={setWebhookField}
          onDeliveryMethodChange={onDeliveryMethodChange}
          disabled={isProcessing}
          isEditing={isEditing}
        />
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Events</label>
          <input
            ref={atLeastOneEventValidationRef}
            type="checkbox"
            required
            checked={webhook.events.length > 0}
            onChange={() => {}}
            onInvalid={(e) => {
              (e.target as HTMLInputElement).setCustomValidity("Please select at least one event");
            }}
            style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
          />
          <EventGroups webhook={webhook} handleEventChange={handleEventChange} disabled={isProcessing} />
        </div>
        <div className="mt-8 flex justify-end">
          {isEditing && (
            <Button
              className="mr-3"
              type={ButtonStyle.TERTIARY}
              disabled={isProcessing}
              onClick={() => navigate("/webhooks")}
              text="Cancel"
            />
          )}
          <Button
            ref={buttonRef}
            type={ButtonStyle.PRIMARY}
            disabled={isProcessing}
            text={
              isProcessing ? (
                <div className="flex">
                  <Spinner.Inline className="mr-3 text-white" /> Saving Webhook...{" "}
                </div>
              ) : (
                "Save Webhook"
              )
            }
            submit
          />
        </div>
      </Form>
    </div>
  );
};

WebhookForm.displayName = "WebhookForm";

export default WebhookForm;
